import { addDays, isBefore, isSameDay, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import type { TaskBucket } from './types';

interface TaskLike {
  due_at: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
}

/**
 * Decide em que bucket do "Meu Dia" uma tarefa cai.
 * - completed/cancelled → não devem aparecer (chamador filtra antes).
 * - sem due_at → 'no_due_date'.
 * - due_at antes de hoje 00:00 → 'overdue'.
 * - due_at hoje → 'today'.
 * - due_at em ≤48h após hoje → 'next_48h'.
 * - depois → 'later'.
 */
export function bucketForTask(task: TaskLike, now: Date = new Date()): TaskBucket {
  if (!task.due_at) return 'no_due_date';
  const due = new Date(task.due_at);
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  if (isSameDay(due, now)) return 'today';
  if (isBefore(due, todayStart)) return 'overdue';
  // Janela "próximas 48h" = amanhã 00:00 até depois-de-amanhã 23:59
  const next48Start = addDays(todayEnd, 0); // logo após hoje 23:59
  const next48End = endOfDay(addDays(now, 2));
  if (isWithinInterval(due, { start: next48Start, end: next48End })) return 'next_48h';
  return 'later';
}

export interface BucketedTasks<T extends TaskLike> {
  overdue: T[];
  today: T[];
  next_48h: T[];
  later: T[];
  no_due_date: T[];
  recentlyCompleted: T[];
}

/**
 * Agrupa tarefas em buckets do Meu Dia.
 * Tarefas concluídas hoje vão para `recentlyCompleted` (motivacional).
 * Tarefas concluídas/canceladas em outros dias são ignoradas.
 */
export function bucketTasks<T extends TaskLike & { completed_at?: string | null }>(
  tasks: T[],
  now: Date = new Date(),
): BucketedTasks<T> {
  const result: BucketedTasks<T> = {
    overdue: [],
    today: [],
    next_48h: [],
    later: [],
    no_due_date: [],
    recentlyCompleted: [],
  };

  for (const t of tasks) {
    if (t.status === 'cancelled') continue;
    if (t.status === 'completed') {
      if (t.completed_at && isSameDay(new Date(t.completed_at), now)) {
        result.recentlyCompleted.push(t);
      }
      continue;
    }
    const bucket = bucketForTask(t, now);
    result[bucket].push(t);
  }

  return result;
}
